'use client'

import { useDocumentApi } from '@repo/sdk'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  DialogTrigger,
  DroppedDocuments,
  Dropzone,
  useDropzoneContext,
} from '@repo/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { ReactNode, useState } from 'react'

interface IProps {
  inquiryId: number
  loadId: number
  queryKey: string
  trigger?: ReactNode
}

export const DropzoneModal = ({ inquiryId, loadId, trigger }: IProps) => {
  const [open, setOpen] = useState<boolean>(false)
  const Trigger = () => trigger ?? null
  const { documents, setDocuments } = useDropzoneContext()
  const { assignDocumentsToLoad } = useDocumentApi()
  const pathname = usePathname()

  const queryClient = useQueryClient()
  const onUpload = async () => {
    try {
      await assignDocumentsToLoad(loadId, documents)
      setDocuments([])
      await queryClient.invalidateQueries({
        queryKey: [
          pathname.includes('loads') ? '/loads' : '/inquiry-loads',
          pathname.includes('loads') ? loadId : inquiryId,
        ],
      })
      setOpen(false)
    } catch (e: unknown) {
      console.error(e)
    }
  }

  const onCancel = () => {
    setDocuments([])
    setOpen(false)
  }

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      {!trigger ? (
        <DialogTrigger
          className={
            'flex items-center gap-2 rounded-md bg-brand px-3 py-1.5 font-medium text-white'
          }
        >
          <Plus size={16} /> 'Upload documents'
        </DialogTrigger>
      ) : (
        <DialogTrigger>
          <Trigger />
        </DialogTrigger>
      )}
      <DialogOverlay className={'!opacity-30'} />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload documents</DialogTitle>
          <DialogClose />
        </DialogHeader>
        <Dropzone />
        <DroppedDocuments className="col-span-2" />
        <DialogFooter>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            onClick={() => {
              onUpload()
            }}
          >
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
